import React from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "./hooks/useDocumentMeta";
import { SiteHeader } from "./components/SiteHeader";
import { SiteFooter } from "./components/SiteFooter";
import { MiniLeadForm } from "./components/MiniLeadForm";

/**
 * NotFound (Section 15)
 *
 * The catch-all 404 page. Shared header and footer per the "identical
 * chrome" directive; warm palette confined to the main content area.
 *
 * Palette tokens (per spec):
 *   --bg          #120D0B
 *   --panel       #1B1411
 *   --line        #2C211C
 *   --grad-start  #8B4A2B
 *   --grad-end    #F15A24
 *   --text        #F5F1EE
 *   --muted       #9C8A82
 *   --dot-off     #3A2D27
 *
 * The main visual is a horizontal three-stage order tracker (Placed,
 * Packed, Stuck). The Stuck stage has a pulsing halo. Motion respects
 * `prefers-reduced-motion`. The tracker never wraps or overflows: it
 * scales down proportionally on smaller viewports.
 */

const NotFound = () => {
  useDocumentMeta({
    title: "Page not found | PickQuick",
    description:
      "This page could not be found. Head back to the PickQuick homepage or continue to QC Consulting, For Platforms, or the blog.",
    ogType: "website",
    // 404 must not be indexed. `follow` still lets crawlers explore
    // the internal links back to real pages.
    robots: "noindex, follow",
  });

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#120D0B", color: "#F5F1EE" }}>
      {/* Local styles: warm palette, order tracker, pulse animation.
          `prefers-reduced-motion` disables the pulse. */}
      <style>{`
        .nf-content {
          position: relative;
          background: #120D0B;
          overflow: hidden;
        }
        .nf-content::before,
        .nf-content::after {
          content: "";
          position: absolute;
          pointer-events: none;
          border-radius: 50%;
        }
        /* Upper-left orange wash, ~6% opacity, fades before centre. */
        .nf-content::before {
          top: -140px;
          left: -120px;
          width: 620px;
          height: 620px;
          background: radial-gradient(circle at center, rgba(241, 90, 36, 0.14) 0%, rgba(241, 90, 36, 0) 65%);
          opacity: 1;
        }
        /* Lower-right brown wash, ~8% opacity, fades before centre. */
        .nf-content::after {
          bottom: -180px;
          right: -160px;
          width: 720px;
          height: 720px;
          background: radial-gradient(circle at center, rgba(139, 74, 43, 0.16) 0%, rgba(139, 74, 43, 0) 65%);
          opacity: 1;
        }

        .nf-pill {
          display: inline-flex;
          align-items: center;
          padding: 6px 18px;
          border-radius: 20px;
          border: 1px solid #2C211C;
          color: #9C8A82;
          background: transparent;
          font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          white-space: nowrap;
        }

        /* Order tracker. Fixed layout using CSS grid so nothing wraps at
           any width; the whole tracker scales via a max-width and inline
           padding on the enclosing section. */
        .nf-tracker {
          width: 100%;
          max-width: 520px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: auto 1fr auto 1fr auto;
          align-items: center;
          gap: 0;
        }
        .nf-dot {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          position: relative;
          flex-shrink: 0;
        }
        .nf-dot--done {
          background: linear-gradient(135deg, #8B4A2B 0%, #F15A24 100%);
        }
        .nf-dot--stuck {
          background: #1B1411;
          border: 2px solid #F15A24;
        }
        .nf-dot--stuck::before {
          content: "";
          position: absolute;
          inset: -10px;
          border-radius: 50%;
          border: 2px solid rgba(241, 90, 36, 0.55);
          animation: nfPulse 1.8s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .nf-dot--stuck::before {
            animation: none;
            border-color: rgba(241, 90, 36, 0.35);
          }
        }
        @keyframes nfPulse {
          0%   { transform: scale(0.85); opacity: 0.85; }
          50%  { transform: scale(1.35); opacity: 0.15; }
          100% { transform: scale(0.85); opacity: 0.85; }
        }
        .nf-connector-solid {
          height: 2px;
          width: 100%;
          background: linear-gradient(90deg, #8B4A2B 0%, #F15A24 100%);
          min-width: 40px;
        }
        .nf-connector-dashed {
          height: 0;
          width: 100%;
          min-width: 40px;
          border-top: 2px dashed rgba(241, 90, 36, 0.7);
        }

        .nf-labels {
          width: 100%;
          max-width: 520px;
          margin: 12px auto 0;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 0;
        }
        .nf-label {
          font-size: 11px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #9C8A82;
          text-align: center;
        }
        .nf-label:first-child { text-align: left; }
        .nf-label:last-child  { text-align: right; }
        .nf-label--active {
          color: #F5F1EE;
          font-weight: 600;
        }

        /* Compact scale on very narrow viewports. Still no wrapping. */
        @media (max-width: 480px) {
          .nf-tracker { max-width: 340px; }
          .nf-labels  { max-width: 340px; }
          .nf-dot     { width: 22px; height: 22px; }
          .nf-connector-solid, .nf-connector-dashed { min-width: 24px; }
        }

        .nf-headline {
          font-size: 28px;
          font-weight: 800;
          letter-spacing: -0.02em;
          line-height: 1.15;
          color: #F5F1EE;
          margin-top: 44px;
          max-width: 560px;
          margin-left: auto;
          margin-right: auto;
          text-align: center;
        }
        .nf-subhead {
          font-size: 15.5px;
          color: #9C8A82;
          line-height: 1.65;
          max-width: 460px;
          margin: 16px auto 0;
          text-align: center;
        }

        .nf-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 48px;
          padding: 0 28px;
          border-radius: 12px;
          color: #FFFFFF;
          font-size: 15px;
          font-weight: 600;
          text-decoration: none;
          background: linear-gradient(135deg, #8B4A2B 0%, #F15A24 100%);
          box-shadow: 0 8px 30px rgba(241, 90, 36, 0.22);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .nf-cta:hover {
          transform: translateY(-1px);
          box-shadow: 0 12px 34px rgba(241, 90, 36, 0.32);
        }

        .nf-secondary-links {
          margin-top: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 22px;
          font-size: 13px;
        }
        .nf-secondary-links a {
          color: #F15A24;
          text-decoration: none;
        }
        .nf-secondary-links a:hover { text-decoration: underline; }
        .nf-secondary-sep {
          color: #3A2D27;
        }
      `}</style>

      <SiteHeader />

      <main
        className="nf-content flex-1 flex flex-col items-center justify-center px-6 py-24"
        data-testid="notfound-main"
      >
        <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: "720px" }}>
          {/* Monospace pill */}
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <span className="nf-pill" data-testid="notfound-err-pill">
              ERR_404 &middot; PICKQUICK OPS
            </span>
          </div>

          {/* Three-stage order tracker */}
          <div className="nf-tracker" data-testid="notfound-tracker" role="img" aria-label="Order status: placed, packed, stuck at the dark store.">
            <span className="nf-dot nf-dot--done" data-testid="notfound-dot-placed" aria-hidden="true" />
            <span className="nf-connector-solid" aria-hidden="true" />
            <span className="nf-dot nf-dot--done" data-testid="notfound-dot-packed" aria-hidden="true" />
            <span className="nf-connector-dashed" aria-hidden="true" />
            <span className="nf-dot nf-dot--stuck" data-testid="notfound-dot-stuck" aria-hidden="true" />
          </div>
          <div className="nf-labels" aria-hidden="true">
            <span className="nf-label">Placed</span>
            <span className="nf-label">Packed</span>
            <span className="nf-label nf-label--active">Stuck</span>
          </div>

          {/* Headline and subhead */}
          <h1 className="nf-headline" data-testid="notfound-headline">
            This link got stuck in a rain-order surge.
          </h1>
          <p className="nf-subhead" data-testid="notfound-subhead">
            Demand spiked, ETAs slipped, and this page did not clear the dark store in time. Standard stuff around here, honestly.
          </p>

          {/* Primary CTA + secondary links */}
          <div style={{ textAlign: "center", marginTop: "36px" }}>
            <Link to="/" className="nf-cta" data-testid="notfound-home-cta">
              Back to homepage
            </Link>
          </div>
          <div className="nf-secondary-links" data-testid="notfound-secondary-links">
            <Link to="/consulting" data-testid="notfound-link-consulting">QC Consulting</Link>
            <span className="nf-secondary-sep" aria-hidden="true">&middot;</span>
            <Link to="/for-quick-commerce" data-testid="notfound-link-platforms">For Platforms</Link>
          </div>
        </div>
      </main>

      <section className="pb-16" style={{ backgroundColor: "#120D0B" }}>
        <div className="container" style={{ maxWidth: "1080px" }}>
          <MiniLeadForm
            source="not-found-mini"
            variant="band"
            pagePath="/404"
            headline="Since you are here, want a free QC audit?"
            subhead="One platform, one category, no cost. We open WhatsApp with your details pre-filled."
          />
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default NotFound;
